import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';

import {EmailNotVerifiedError, useAuthStore} from '@/entities/session/auth-store';
import {ApiError} from '@/shared/api';
import {messageOf} from '@/shared/lib/errors';
import {oauthEnabled} from '@/shared/config/oauth';
import {GOOGLE_SIGNIN_AVAILABLE} from '@/shared/lib/google-signin';
import type {AuthNav} from '@/app/routes';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {AppInput} from '@/shared/ui/input';
import {Logo} from '@/shared/ui/logo';
import {Screen} from '@/shared/ui/screen';
import {AppText, useLinkText} from '@/shared/ui/text';
import {controls, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Sign-in. The identifier field takes email, username or phone in one box because
 * sv_auth's LoginRequest takes one `identifier` and works out which it is — asking
 * the user to first classify their own login would be us leaking our schema.
 *
 * A wrong password is printed under the fields and STAYS there. A toast that fades
 * after three seconds is exactly the wrong affordance for the one message the user
 * needs while re-reading what they typed.
 *
 * The screen is a hero + a card: the mark and the product name establish where you
 * are before you type a credential into it, which matters more here than anywhere
 * else in the app — this is the one page a phishing lookalike would target.
 */
const styles = StyleSheet.create({
  body: {justifyContent: 'center', gap: spacing(5), paddingVertical: spacing(8)},
  hero: {alignItems: 'center', gap: spacing(3)},
  heroText: {alignItems: 'center', gap: spacing(1), paddingHorizontal: spacing(4)},
  centered: {textAlign: 'center'},
  form: {gap: spacing(4)},
  links: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing(3),
  },
  /* Ссылка — тоже цель для пальца: в вебе `hitSlop` не работает, так что 44pt
     набирает сама коробка. */
  link: {minHeight: controls.lg, justifyContent: 'center'},
  divider: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  rule: {flex: 1, height: StyleSheet.hairlineWidth},
});

export function LoginScreen() {
  const {t} = useTranslation();
  const p = useTheme();
  const link = useLinkText();
  const nav = useNavigation<AuthNav>();
  const login = useAuthStore(s => s.login);
  const loginWithGoogle = useAuthStore(s => s.loginWithGoogle);

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  // Two conditions, both necessary. `oauthEnabled` is deployment configuration (is
  // there a client id at all); `GOOGLE_SIGNIN_AVAILABLE` is the platform (the browser
  // half is complete, the native half is a documented seam). A button that is offered
  // and then always fails is worse than no button — see shared/lib/google-signin.ts.
  const showGoogle = oauthEnabled() && GOOGLE_SIGNIN_AVAILABLE;

  async function submitGoogle() {
    setError(null);
    setGoogleBusy(true);
    try {
      // null ⇒ the user closed the provider's dialog. Deliberate, not an error, and
      // showing a red banner for it is how a button loses people's trust.
      await loginWithGoogle();
    } catch (e) {
      setError(messageOf(e, t('auth.errors.googleFailed')));
    } finally {
      setGoogleBusy(false);
    }
  }

  async function submit() {
    if (!identifier.trim()) {
      setError(t('auth.errors.identifierRequired'));
      return;
    }
    if (!password) {
      setError(t('auth.errors.passwordRequired'));
      return;
    }

    setError(null);
    setBusy(true);
    try {
      await login(identifier.trim(), password);
      // No navigation call: the store's status flips and the root navigator swaps
      // the whole stack. Screens do not decide where the session goes.
    } catch (e) {
      // Регистрацию бросили до кода — новый код уже ушёл, ведём на экран кода.
      if (e instanceof EmailNotVerifiedError) {
        nav.navigate('VerifyEmail');
        return;
      }
      // 401 is not a server fault the user should read as one — name it plainly.
      setError(
        e instanceof ApiError && e.status === 401
          ? t('auth.errors.invalidCredentials')
          : messageOf(e, t('errors.network')),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen scroll width="narrow" style={styles.body} testID="login-screen">
      <View style={styles.hero}>
        {/* The brand, as the brand — not a shield glyph with the name typed under it.
            This is the one screen a phishing lookalike would target, so what the user
            recognises has to be the real asset. */}
        <Logo width={230} />
        <View style={styles.heroText}>
          <AppText variant="body" color={p.mutedForeground} style={styles.centered}>
            {t('auth.login.subtitle')}
          </AppText>
        </View>
      </View>

      <Card padding={5} style={styles.form}>
        <AppInput
          testID="login-identifier"
          label={t('auth.login.identifier')}
          placeholder={t('auth.login.identifierPlaceholder')}
          value={identifier}
          onChangeText={setIdentifier}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="email-address"
          textContentType="username"
          icon="mail"
        />

        <AppInput
          testID="login-password"
          label={t('auth.login.password')}
          value={password}
          onChangeText={setPassword}
          secure
          autoCapitalize="none"
          textContentType="password"
          icon="lock"
          showPasswordLabel={t('a11y.showPassword')}
          hidePasswordLabel={t('a11y.hidePassword')}
          onSubmitEditing={submit}
          returnKeyType="go"
        />

        {error ? (
          <Banner testID="login-error" tone="error" title={error} />
        ) : null}

        <AppButton
          testID="login-submit"
          title={t('auth.login.submit')}
          size="lg"
          onPress={submit}
          loading={busy}
        />

        {showGoogle ? (
          <>
            <View style={styles.divider}>
              <View style={[styles.rule, {backgroundColor: p.border}]} />
              <AppText variant="caption">{t('auth.login.or')}</AppText>
              <View style={[styles.rule, {backgroundColor: p.border}]} />
            </View>
            <AppButton
              testID="login-google"
              title={t('auth.login.google')}
              tone="neutral"
              size="lg"
              onPress={submitGoogle}
              loading={googleBusy}
            />
          </>
        ) : null}

        <View style={styles.links}>
          <Pressable
            testID="link-register"
            style={styles.link}
            accessibilityRole="link"
            onPress={() => nav.navigate('Register')}>
            <AppText variant="label" style={link}>
              {t('auth.login.toRegister')}
            </AppText>
          </Pressable>
          <Pressable
            testID="link-forgot"
            style={styles.link}
            accessibilityRole="link"
            onPress={() => nav.navigate('Forgot')}>
            <AppText variant="label" style={link}>
              {t('auth.login.toForgot')}
            </AppText>
          </Pressable>
        </View>
      </Card>
    </Screen>
  );
}
